import { memo } from 'react';

import { Button, formatNumber, PageTitle } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { BtnGroup } from '@dec/shared';
import { Row } from '@dec/shared';
import { type ColDef, type ValueFormatterParams } from 'ag-grid-community';
import { downloadFile } from 'app/components/form/file/file.sevice';
import GridApplication, { GridContainer, OnlyGrid } from 'app/components/grid/gridApplication';
import saveAs from 'file-saver';
import { Col, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import DateParser from 'utils/date';

import { useGetDefinitionExcel } from '../hooks/useGetDefinitionExcel';
import { useGetPaymentsReceived } from '../hooks/useGetPaymentsReceived';
import { paymentFileDetailsSearch } from '../models/Payment';

interface Props {
  hideCancel?: boolean;
}

export const UploadDetails = memo((props: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<Record<string, string>>();
  const { data, loading, error } = useGetPaymentsReceived(id ?? '', true);

  const {
    data: attachmentDef,
    loading: loadingAttachmentDef,
    error: errorAttachment
  } = useGetDefinitionExcel(true);

  const colDefs = [
    {
      headerName: t(`common.label.header`),
      field: 'header'
    },
    {
      headerName: t('common.label.type'),
      cellRenderer: (params: any) => t(`common.label.${params.data.type}`)
    },
    {
      headerName: t(`common.label.description`),
      field: 'description'
    }
  ];

  const download = async () => {
    if (!data) return;
    const response: any = await downloadFile(data.attachment);
    fetch('data:application/octet-stream;base64,' + response.content)
      .then((res) => res.blob())
      .then((blob: any) => saveAs(blob, response.name));
  };

  const colDefsDetail: Array<ColDef> = [
    {
      headerName: 'DATE',
      field: 'date',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ? DateParser.toString(params.value) : '---';
      }
    },
    {
      headerName: 'CURRENCY',
      field: 'currencyCode',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ?? '---';
      }
    },
    {
      headerName: 'AMOUNT',
      field: 'amount',
      cellStyle: (params: any) => {
        return { 'text-align': 'right' };
      },
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ? formatNumber(params.value) : '---';
      }
    },
    {
      headerName: 'TAXPAYER',
      field: 'taxpayer',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ?? '---';
      }
    },
    {
      headerName: 'BANK_COST',
      field: 'bankCost',
      cellStyle: (params: any) => {
        return { 'text-align': 'right' };
      },
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ? formatNumber(params.value) : '---';
      }
    },
    {
      headerName: 'REFERENCE',
      field: 'reference',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ?? '---';
      }
    },
    {
      headerName: 'SWIFT',
      field: 'swift',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ?? '---';
      }
    },
    {
      headerName: t('taxpayer-payments.label.hasErrors')?.toUpperCase(),
      cellRenderer: (params: any) => {
        return params.data.validationResults?.length > 0
          ? t('common.yes-no.yes')
          : t('common.yes-no.no');
      }
    },
    {
      headerName: t('common.dialog.dialog-error')?.toUpperCase(),
      field: 'validationResultsText'
    }
  ];
  const loadingAll = loadingAttachmentDef || error || loading;
  if (loadingAll) {
    return <></>;
  }
  return (
    <>
      <PageTitle title={t('taxpayer-payments.title.page')} />

      <Row>
        <Col>
          <Form.Group controlId="reportNumber">
            <Form.Label>{t('taxpayer-payments.label.uploadNumber')}</Form.Label>
            <Form.Control type="text" disabled value={data?.uploadNumber ?? ''} />
          </Form.Group>
        </Col>

        <Col>
          <Form.Group controlId="period">
            <Form.Label>{t('taxpayer-payments.label.taxCollector')}</Form.Label>
            <Form.Control type="text" disabled value={data?.taxCollector?.name ?? ''} />
          </Form.Group>
        </Col>
      </Row>

      <hr></hr>
      <Row>
        <Col>
          <GridContainer autoHeight={true}>
            <OnlyGrid domLayout={'autoHeight'} columnDefs={colDefs} data={attachmentDef ?? []} />
          </GridContainer>
        </Col>
      </Row>
      <hr />
      <Row>
        <Col>
          <GridApplication
            url={`/taxreturnpayments/received/detail`}
            columnDefs={colDefsDetail}
            search={paymentFileDetailsSearch(id ?? '')}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <hr />
        </Col>
      </Row>
      <BtnToolbar>
        <BtnGroup>
          {!props.hideCancel && (
            <Button
              action={ButtonAction.cancel}
              variant="secondary"
              onClick={() => {
                navigate(-1);
              }}
            />
          )}
        </BtnGroup>
        <BtnGroup>
          <Button
            action={ButtonAction.download}
            onClick={() => {
              download();
            }}
          />
        </BtnGroup>
      </BtnToolbar>
    </>
  );
});

UploadDetails.displayName = 'UploadDetails';
